/**
 * One display model for a cycle's beta-hCG measurements, whatever modality
 * produced them.
 *
 * A beta is the same clinical fact on an FET, an IVF, an IUI or an OI cycle,
 * but the app used to read it from a different place on each: the FET sidecar
 * carried its own typed series, and every other modality left the number in a
 * generic lab row. Two readers meant two sets of rules for what a value, a
 * unit and a collection time mean, so patients on different treatments saw the
 * same measurement described differently (#3490).
 *
 * The canonical cycle series (`GET /cycles/{cycleId}/beta-hcg-observations`)
 * is now the single measurement source. It arrives deduplicated and in
 * deterministic chronological order, so this module does not re-sort it —
 * a client-side reorder could only disagree with the server that owns the
 * rule.
 *
 * Measurement only. Order state, missed and cancelled draws, and the clinic's
 * interpretations are workflow facts that stay on the FET order endpoints.
 */

import type { paths } from "@src/packages/openapi/schema";

type BetaHcgSeriesResponse =
  paths["/cycles/{cycleId}/beta-hcg-observations"]["get"]["responses"]["200"]["content"]["application/json"];

export type BetaHcgSeriesItem = BetaHcgSeriesResponse["data"][number];

/**
 * When the sample was taken, at the precision it was actually recorded at.
 *
 * `unknown` is a real answer and the reason this is a union rather than a
 * date string: a patient who recorded a beta without the time of the draw has
 * a day, and a legacy row may not even have that. Collapsing either onto a
 * timestamp would print a time nobody recorded.
 */
export type BetaHcgCollectionTime =
  /** No collection time on the record. Do not substitute the result time. */
  | { kind: "unknown" }
  /** A calendar day, already resolved in the recorded zone by the server. */
  | { kind: "day"; date: string }
  | { kind: "month"; iso: string }
  | { kind: "year"; iso: string }
  /** A known instant; `minute` precision still renders a clock time. */
  | { kind: "dateTime"; iso: string; timezone: string | null };

export type BetaHcgDisplayRow = {
  id: string;
  /** Null while a legacy row has no canonical observation yet. */
  observationId: string | null;
  /** The FET order's draw number. Null for every other modality. */
  sequence: number | null;
  /** Null when the record carries no measured value. */
  value: number | null;
  /** What was originally typed, when it differs from the parsed number. */
  rawValue: string | null;
  unit: string | null;
  collectedAt: BetaHcgCollectionTime;
  /** When the result was issued. Never a stand-in for the collection time. */
  issuedAt: string | null;
  status: BetaHcgSeriesItem["status"];
  reviewStatus: BetaHcgSeriesItem["reviewStatus"];
  /**
   * `unknown` marks a legacy row whose value-level provenance was never
   * recorded — a surface may not attribute it to the patient or the clinic.
   */
  provenanceStatus: BetaHcgSeriesItem["provenanceStatus"];
  source: string | null;
  sourceKind: string | null;
  /** This measurement corrects an earlier one rather than replacing it. */
  isCorrection: boolean;
  /** Still read through the compatibility fallback, not yet converted. */
  isUnconvertedLegacy: boolean;
};

function collectionTimeOf(item: BetaHcgSeriesItem): BetaHcgCollectionTime {
  if (item.effectiveDate) return { kind: "day", date: item.effectiveDate };
  if (!item.effectiveAt) return { kind: "unknown" };

  switch (item.datePrecision) {
    case "year":
      return { kind: "year", iso: item.effectiveAt };
    case "month":
      return { kind: "month", iso: item.effectiveAt };
    // A `day`-precision row reaches the client as `effectiveDate` and is
    // handled above; this branch only catches one that did not.
    case "day":
      return { kind: "day", date: item.effectiveAt.slice(0, 10) };
    default:
      return {
        kind: "dateTime",
        iso: item.effectiveAt,
        timezone: item.timezone,
      };
  }
}

export function toBetaHcgDisplayRow(
  item: BetaHcgSeriesItem,
): BetaHcgDisplayRow {
  return {
    id: item.id,
    observationId: item.clinicalObservationId,
    sequence: item.sequence,
    value: item.value,
    // Only worth showing when the parsed number is not the whole story: "<1"
    // and "1.8" both arrive with a `value`, and only the first says something
    // the number cannot.
    rawValue:
      item.rawValue != null && item.rawValue !== String(item.value)
        ? item.rawValue
        : null,
    unit: item.unit,
    collectedAt: collectionTimeOf(item),
    issuedAt: item.issuedAt,
    status: item.status,
    reviewStatus: item.reviewStatus,
    provenanceStatus: item.provenanceStatus,
    source: item.source,
    sourceKind: item.sourceKind,
    isCorrection: item.supersedesObservationId != null,
    isUnconvertedLegacy: item.compatibilitySource != null,
  };
}

/**
 * The rows a measurement surface should draw, in the order the server gave
 * them.
 *
 * `entered_in_error` and `refuted` rows are dropped: both are the record
 * saying this measurement is not a measurement. A `corrected` row is kept —
 * it is the standing value, and the correction is stated by the lineage edge
 * rather than by withdrawing the number.
 */
export function toBetaHcgDisplayRows(
  series: readonly BetaHcgSeriesItem[] | null | undefined,
): BetaHcgDisplayRow[] {
  return (series ?? [])
    .filter(
      (item) => item.status !== "entered_in_error" && item.status !== "refuted",
    )
    .map(toBetaHcgDisplayRow);
}

/** Whether a row has a number to draw at all. */
export function hasBetaHcgValue(row: BetaHcgDisplayRow): boolean {
  return row.value != null || row.rawValue != null;
}
